import type { ApiResponseCsvView } from "@vega-api/generated/types/apiResponseCsvView";
import type { CSVCellValue } from "@vega-api/generated/types/cSVCellValue";
import type { Meta } from "@vega-api/generated/types/meta";

const createMockMeta = (page = 1, pageSize = 10, totalPages = 1): Meta => ({
  page,
  pageSize,
  totalPages,
});

export const organizationsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Name", "Type", "Country", "Status"],
    rows: [
      {
        ID: { value: "ORG-001" as unknown as CSVCellValue },
        Name: { value: "Acme Corporation" as unknown as CSVCellValue },
        Type: { value: "Corporation" as unknown as CSVCellValue },
        Country: { value: "United States" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ORG-002" as unknown as CSVCellValue },
        Name: { value: "Global Ventures Ltd" as unknown as CSVCellValue },
        Type: { value: "Limited Company" as unknown as CSVCellValue },
        Country: { value: "United Kingdom" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ORG-003" as unknown as CSVCellValue },
        Name: { value: "Pacific Holdings Inc" as unknown as CSVCellValue },
        Type: { value: "Corporation" as unknown as CSVCellValue },
        Country: { value: "Singapore" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ORG-004" as unknown as CSVCellValue },
        Name: { value: "European Capital Group" as unknown as CSVCellValue },
        Type: { value: "Partnership" as unknown as CSVCellValue },
        Country: { value: "Germany" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ORG-005" as unknown as CSVCellValue },
        Name: { value: "Asia Pacific Investments" as unknown as CSVCellValue },
        Type: { value: "Corporation" as unknown as CSVCellValue },
        Country: { value: "Hong Kong" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const usersMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Name", "Email", "Organization", "Role", "Status"],
    rows: [
      {
        ID: { value: "USER-001" as unknown as CSVCellValue },
        Name: { value: "John Smith" as unknown as CSVCellValue },
        Email: { value: "john.smith@acme.com" as unknown as CSVCellValue },
        Organization: { value: "Acme Corporation" as unknown as CSVCellValue },
        Role: { value: "Administrator" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "USER-002" as unknown as CSVCellValue },
        Name: { value: "Sarah Johnson" as unknown as CSVCellValue },
        Email: {
          value: "sarah.johnson@globalventures.com" as unknown as CSVCellValue,
        },
        Organization: {
          value: "Global Ventures Ltd" as unknown as CSVCellValue,
        },
        Role: { value: "User" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "USER-003" as unknown as CSVCellValue },
        Name: { value: "Michael Chen" as unknown as CSVCellValue },
        Email: {
          value: "michael.chen@pacificholdings.com" as unknown as CSVCellValue,
        },
        Organization: {
          value: "Pacific Holdings Inc" as unknown as CSVCellValue,
        },
        Role: { value: "Manager" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "USER-004" as unknown as CSVCellValue },
        Name: { value: "Emma Wilson" as unknown as CSVCellValue },
        Email: {
          value: "emma.wilson@europeancapital.com" as unknown as CSVCellValue,
        },
        Organization: {
          value: "European Capital Group" as unknown as CSVCellValue,
        },
        Role: { value: "User" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "USER-005" as unknown as CSVCellValue },
        Name: { value: "David Lee" as unknown as CSVCellValue },
        Email: {
          value: "david.lee@asiapacific.com" as unknown as CSVCellValue,
        },
        Organization: {
          value: "Asia Pacific Investments" as unknown as CSVCellValue,
        },
        Role: { value: "Administrator" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const userRolesMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["User ID", "Role", "Organization", "Status"],
    rows: [
      {
        "User ID": { value: "USER-001" as unknown as CSVCellValue },
        Role: { value: "Admin" as unknown as CSVCellValue },
        Organization: { value: "ORG-001" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        "User ID": { value: "USER-002" as unknown as CSVCellValue },
        Role: { value: "User" as unknown as CSVCellValue },
        Organization: { value: "ORG-002" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        "User ID": { value: "USER-003" as unknown as CSVCellValue },
        Role: { value: "Viewer" as unknown as CSVCellValue },
        Organization: { value: "ORG-001" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const investingEntityMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Name", "Organization", "Type", "Country", "Status"],
    rows: [
      {
        ID: { value: "IE-001" as unknown as CSVCellValue },
        Name: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        Organization: { value: "Acme Corporation" as unknown as CSVCellValue },
        Type: { value: "Fund" as unknown as CSVCellValue },
        Country: { value: "United States" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IE-002" as unknown as CSVCellValue },
        Name: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        Organization: {
          value: "Global Ventures Ltd" as unknown as CSVCellValue,
        },
        Type: { value: "Fund" as unknown as CSVCellValue },
        Country: { value: "United Kingdom" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IE-003" as unknown as CSVCellValue },
        Name: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        Organization: {
          value: "Pacific Holdings Inc" as unknown as CSVCellValue,
        },
        Type: { value: "Fund" as unknown as CSVCellValue },
        Country: { value: "Singapore" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IE-004" as unknown as CSVCellValue },
        Name: { value: "European Capital Fund III" as unknown as CSVCellValue },
        Organization: {
          value: "European Capital Group" as unknown as CSVCellValue,
        },
        Type: { value: "Fund" as unknown as CSVCellValue },
        Country: { value: "Germany" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IE-005" as unknown as CSVCellValue },
        Name: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        Organization: {
          value: "Asia Pacific Investments" as unknown as CSVCellValue,
        },
        Type: { value: "Fund" as unknown as CSVCellValue },
        Country: { value: "Hong Kong" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const investmentsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: [
      "ID",
      "Investment Name",
      "Fund",
      "Investing Entity",
      "Amount",
      "Currency",
      "Date",
      "Status",
    ],
    rows: [
      {
        ID: { value: "INV-001" as unknown as CSVCellValue },
        "Investment Name": {
          value: "Tech Startup Alpha" as unknown as CSVCellValue,
        },
        Fund: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Acme Investment Fund I" as unknown as CSVCellValue,
        },
        Amount: { value: "5000000" as unknown as CSVCellValue },
        Currency: { value: "USD" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "INV-002" as unknown as CSVCellValue },
        "Investment Name": {
          value: "Healthcare Innovation Co" as unknown as CSVCellValue,
        },
        Fund: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Global Ventures Fund II" as unknown as CSVCellValue,
        },
        Amount: { value: "7500000" as unknown as CSVCellValue },
        Currency: { value: "GBP" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "INV-003" as unknown as CSVCellValue },
        "Investment Name": {
          value: "Fintech Solutions Ltd" as unknown as CSVCellValue,
        },
        Fund: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Amount: { value: "3000000" as unknown as CSVCellValue },
        Currency: { value: "SGD" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "INV-004" as unknown as CSVCellValue },
        "Investment Name": {
          value: "Green Energy Ventures" as unknown as CSVCellValue,
        },
        Fund: { value: "European Capital Fund III" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "European Capital Fund III" as unknown as CSVCellValue,
        },
        Amount: { value: "10000000" as unknown as CSVCellValue },
        Currency: { value: "EUR" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "INV-005" as unknown as CSVCellValue },
        "Investment Name": {
          value: "E-commerce Platform Beta" as unknown as CSVCellValue,
        },
        Fund: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Asia Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Amount: { value: "4000000" as unknown as CSVCellValue },
        Currency: { value: "HKD" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const opportunitiesMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: [
      "ID",
      "Opportunity Name",
      "Fund",
      "Investing Entity",
      "Type",
      "Status",
      "Date",
    ],
    rows: [
      {
        ID: { value: "OPP-001" as unknown as CSVCellValue },
        "Opportunity Name": {
          value: "Series A - Tech Startup Alpha" as unknown as CSVCellValue,
        },
        Fund: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Acme Investment Fund I" as unknown as CSVCellValue,
        },
        Type: { value: "Series A" as unknown as CSVCellValue },
        Status: { value: "Open" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
      },
      {
        ID: { value: "OPP-002" as unknown as CSVCellValue },
        "Opportunity Name": {
          value: "Series B - Healthcare Innovation" as unknown as CSVCellValue,
        },
        Fund: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Global Ventures Fund II" as unknown as CSVCellValue,
        },
        Type: { value: "Series B" as unknown as CSVCellValue },
        Status: { value: "Open" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
      },
      {
        ID: { value: "OPP-003" as unknown as CSVCellValue },
        "Opportunity Name": {
          value: "Seed Round - Fintech Solutions" as unknown as CSVCellValue,
        },
        Fund: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Type: { value: "Seed" as unknown as CSVCellValue },
        Status: { value: "Open" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
      },
      {
        ID: { value: "OPP-004" as unknown as CSVCellValue },
        "Opportunity Name": {
          value: "Series C - Green Energy" as unknown as CSVCellValue,
        },
        Fund: { value: "European Capital Fund III" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "European Capital Fund III" as unknown as CSVCellValue,
        },
        Type: { value: "Series C" as unknown as CSVCellValue },
        Status: { value: "Closed" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
      },
      {
        ID: { value: "OPP-005" as unknown as CSVCellValue },
        "Opportunity Name": {
          value: "Series A - E-commerce Platform" as unknown as CSVCellValue,
        },
        Fund: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        "Investing Entity": {
          value: "Asia Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Type: { value: "Series A" as unknown as CSVCellValue },
        Status: { value: "Open" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
      },
    ],
  },
});

export const wireInstructionsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: [
      "ID",
      "Reference",
      "Fund",
      "Amount",
      "Currency",
      "Status",
      "Date",
    ],
    rows: [
      {
        ID: { value: "WI-001" as unknown as CSVCellValue },
        Reference: { value: "WIRE-2024-001" as unknown as CSVCellValue },
        Fund: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        Amount: { value: "1000000" as unknown as CSVCellValue },
        Currency: { value: "USD" as unknown as CSVCellValue },
        Status: { value: "Completed" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
      },
      {
        ID: { value: "WI-002" as unknown as CSVCellValue },
        Reference: { value: "WIRE-2024-002" as unknown as CSVCellValue },
        Fund: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        Amount: { value: "2000000" as unknown as CSVCellValue },
        Currency: { value: "GBP" as unknown as CSVCellValue },
        Status: { value: "Pending" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
      },
      {
        ID: { value: "WI-003" as unknown as CSVCellValue },
        Reference: { value: "WIRE-2024-003" as unknown as CSVCellValue },
        Fund: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        Amount: { value: "1500000" as unknown as CSVCellValue },
        Currency: { value: "SGD" as unknown as CSVCellValue },
        Status: { value: "Completed" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
      },
      {
        ID: { value: "WI-004" as unknown as CSVCellValue },
        Reference: { value: "WIRE-2024-004" as unknown as CSVCellValue },
        Fund: { value: "European Capital Fund III" as unknown as CSVCellValue },
        Amount: { value: "3000000" as unknown as CSVCellValue },
        Currency: { value: "EUR" as unknown as CSVCellValue },
        Status: { value: "Completed" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
      },
      {
        ID: { value: "WI-005" as unknown as CSVCellValue },
        Reference: { value: "WIRE-2024-005" as unknown as CSVCellValue },
        Fund: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        Amount: { value: "800000" as unknown as CSVCellValue },
        Currency: { value: "HKD" as unknown as CSVCellValue },
        Status: { value: "Pending" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
      },
    ],
  },
});

export const limitedPartnersMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Name", "Fund", "Commitment", "Currency", "Status"],
    rows: [
      {
        ID: { value: "LP-001" as unknown as CSVCellValue },
        Name: { value: "Pension Fund Alpha" as unknown as CSVCellValue },
        Fund: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        Commitment: { value: "5000000" as unknown as CSVCellValue },
        Currency: { value: "USD" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "LP-002" as unknown as CSVCellValue },
        Name: { value: "Endowment Beta" as unknown as CSVCellValue },
        Fund: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        Commitment: { value: "7500000" as unknown as CSVCellValue },
        Currency: { value: "GBP" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "LP-003" as unknown as CSVCellValue },
        Name: { value: "Family Office Gamma" as unknown as CSVCellValue },
        Fund: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        Commitment: { value: "3000000" as unknown as CSVCellValue },
        Currency: { value: "SGD" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "LP-004" as unknown as CSVCellValue },
        Name: { value: "Insurance Company Delta" as unknown as CSVCellValue },
        Fund: { value: "European Capital Fund III" as unknown as CSVCellValue },
        Commitment: { value: "10000000" as unknown as CSVCellValue },
        Currency: { value: "EUR" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
      {
        ID: { value: "LP-005" as unknown as CSVCellValue },
        Name: {
          value: "Sovereign Wealth Fund Epsilon" as unknown as CSVCellValue,
        },
        Fund: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        Commitment: { value: "4000000" as unknown as CSVCellValue },
        Currency: { value: "HKD" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
      },
    ],
  },
});

export const investmentMetricsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Investment", "Metric", "Value", "Period", "Date"],
    rows: [
      {
        ID: { value: "IM-001" as unknown as CSVCellValue },
        Investment: { value: "Tech Startup Alpha" as unknown as CSVCellValue },
        Metric: { value: "IRR" as unknown as CSVCellValue },
        Value: { value: "25.5%" as unknown as CSVCellValue },
        Period: { value: "Q1 2024" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IM-002" as unknown as CSVCellValue },
        Investment: {
          value: "Healthcare Innovation Co" as unknown as CSVCellValue,
        },
        Metric: { value: "MOIC" as unknown as CSVCellValue },
        Value: { value: "2.3x" as unknown as CSVCellValue },
        Period: { value: "Q1 2024" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IM-003" as unknown as CSVCellValue },
        Investment: {
          value: "Fintech Solutions Ltd" as unknown as CSVCellValue,
        },
        Metric: { value: "IRR" as unknown as CSVCellValue },
        Value: { value: "18.2%" as unknown as CSVCellValue },
        Period: { value: "Q1 2024" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IM-004" as unknown as CSVCellValue },
        Investment: {
          value: "Green Energy Ventures" as unknown as CSVCellValue,
        },
        Metric: { value: "MOIC" as unknown as CSVCellValue },
        Value: { value: "1.8x" as unknown as CSVCellValue },
        Period: { value: "Q1 2024" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
      },
      {
        ID: { value: "IM-005" as unknown as CSVCellValue },
        Investment: {
          value: "E-commerce Platform Beta" as unknown as CSVCellValue,
        },
        Metric: { value: "IRR" as unknown as CSVCellValue },
        Value: { value: "22.1%" as unknown as CSVCellValue },
        Period: { value: "Q1 2024" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
      },
    ],
  },
});

export const investorDocumentsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: ["ID", "Document Name", "Investor", "Type", "Status", "Date"],
    rows: [
      {
        ID: { value: "ID-001" as unknown as CSVCellValue },
        "Document Name": {
          value: "Subscription Agreement - Fund I" as unknown as CSVCellValue,
        },
        Investor: { value: "Pension Fund Alpha" as unknown as CSVCellValue },
        Type: { value: "Subscription" as unknown as CSVCellValue },
        Status: { value: "Signed" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ID-002" as unknown as CSVCellValue },
        "Document Name": {
          value: "Side Letter - Fund II" as unknown as CSVCellValue,
        },
        Investor: { value: "Endowment Beta" as unknown as CSVCellValue },
        Type: { value: "Side Letter" as unknown as CSVCellValue },
        Status: { value: "Pending" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ID-003" as unknown as CSVCellValue },
        "Document Name": {
          value: "KYC Documentation - Fund III" as unknown as CSVCellValue,
        },
        Investor: { value: "Family Office Gamma" as unknown as CSVCellValue },
        Type: { value: "KYC" as unknown as CSVCellValue },
        Status: { value: "Completed" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ID-004" as unknown as CSVCellValue },
        "Document Name": {
          value: "Redemption Notice - Fund IV" as unknown as CSVCellValue,
        },
        Investor: {
          value: "Insurance Company Delta" as unknown as CSVCellValue,
        },
        Type: { value: "Redemption" as unknown as CSVCellValue },
        Status: { value: "Processed" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
      },
      {
        ID: { value: "ID-005" as unknown as CSVCellValue },
        "Document Name": {
          value: "Transfer Agreement - Fund V" as unknown as CSVCellValue,
        },
        Investor: {
          value: "Sovereign Wealth Fund Epsilon" as unknown as CSVCellValue,
        },
        Type: { value: "Transfer" as unknown as CSVCellValue },
        Status: { value: "Signed" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
      },
    ],
  },
});

export const capitalActivitiesMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: [
      "ID",
      "Activity Type",
      "Fund",
      "Investor",
      "Amount",
      "Currency",
      "Date",
    ],
    rows: [
      {
        ID: { value: "CA-001" as unknown as CSVCellValue },
        "Activity Type": { value: "Capital Call" as unknown as CSVCellValue },
        Fund: { value: "Acme Investment Fund I" as unknown as CSVCellValue },
        Investor: { value: "Pension Fund Alpha" as unknown as CSVCellValue },
        Amount: { value: "1000000" as unknown as CSVCellValue },
        Currency: { value: "USD" as unknown as CSVCellValue },
        Date: { value: "2024-01-15" as unknown as CSVCellValue },
      },
      {
        ID: { value: "CA-002" as unknown as CSVCellValue },
        "Activity Type": { value: "Distribution" as unknown as CSVCellValue },
        Fund: { value: "Global Ventures Fund II" as unknown as CSVCellValue },
        Investor: { value: "Endowment Beta" as unknown as CSVCellValue },
        Amount: { value: "500000" as unknown as CSVCellValue },
        Currency: { value: "GBP" as unknown as CSVCellValue },
        Date: { value: "2024-02-20" as unknown as CSVCellValue },
      },
      {
        ID: { value: "CA-003" as unknown as CSVCellValue },
        "Activity Type": { value: "Capital Call" as unknown as CSVCellValue },
        Fund: { value: "Pacific Growth Fund" as unknown as CSVCellValue },
        Investor: { value: "Family Office Gamma" as unknown as CSVCellValue },
        Amount: { value: "750000" as unknown as CSVCellValue },
        Currency: { value: "SGD" as unknown as CSVCellValue },
        Date: { value: "2024-03-10" as unknown as CSVCellValue },
      },
      {
        ID: { value: "CA-004" as unknown as CSVCellValue },
        "Activity Type": { value: "Distribution" as unknown as CSVCellValue },
        Fund: { value: "European Capital Fund III" as unknown as CSVCellValue },
        Investor: {
          value: "Insurance Company Delta" as unknown as CSVCellValue,
        },
        Amount: { value: "2000000" as unknown as CSVCellValue },
        Currency: { value: "EUR" as unknown as CSVCellValue },
        Date: { value: "2024-04-05" as unknown as CSVCellValue },
      },
      {
        ID: { value: "CA-005" as unknown as CSVCellValue },
        "Activity Type": { value: "Capital Call" as unknown as CSVCellValue },
        Fund: { value: "Asia Pacific Growth Fund" as unknown as CSVCellValue },
        Investor: {
          value: "Sovereign Wealth Fund Epsilon" as unknown as CSVCellValue,
        },
        Amount: { value: "400000" as unknown as CSVCellValue },
        Currency: { value: "HKD" as unknown as CSVCellValue },
        Date: { value: "2024-05-12" as unknown as CSVCellValue },
      },
    ],
  },
});

export const fundsMockData = (): ApiResponseCsvView => ({
  meta: createMockMeta(1, 10, 1),
  errors: null,
  data: {
    headers: [
      "ID",
      "Fund Name",
      "Vehicle",
      "Share Class",
      "Status",
      "Launch Date",
    ],
    rows: [
      {
        ID: { value: "FUND-001" as unknown as CSVCellValue },
        "Fund Name": {
          value: "Acme Investment Fund I" as unknown as CSVCellValue,
        },
        Vehicle: { value: "Acme Fund Vehicle I" as unknown as CSVCellValue },
        "Share Class": { value: "Class A" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
        "Launch Date": { value: "2023-01-01" as unknown as CSVCellValue },
      },
      {
        ID: { value: "FUND-002" as unknown as CSVCellValue },
        "Fund Name": {
          value: "Global Ventures Fund II" as unknown as CSVCellValue,
        },
        Vehicle: { value: "Global Fund Vehicle II" as unknown as CSVCellValue },
        "Share Class": { value: "Class B" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
        "Launch Date": { value: "2023-02-01" as unknown as CSVCellValue },
      },
      {
        ID: { value: "FUND-003" as unknown as CSVCellValue },
        "Fund Name": {
          value: "Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Vehicle: { value: "Pacific Fund Vehicle" as unknown as CSVCellValue },
        "Share Class": { value: "Class A" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
        "Launch Date": { value: "2023-03-01" as unknown as CSVCellValue },
      },
      {
        ID: { value: "FUND-004" as unknown as CSVCellValue },
        "Fund Name": {
          value: "European Capital Fund III" as unknown as CSVCellValue,
        },
        Vehicle: {
          value: "European Fund Vehicle III" as unknown as CSVCellValue,
        },
        "Share Class": { value: "Class C" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
        "Launch Date": { value: "2023-04-01" as unknown as CSVCellValue },
      },
      {
        ID: { value: "FUND-005" as unknown as CSVCellValue },
        "Fund Name": {
          value: "Asia Pacific Growth Fund" as unknown as CSVCellValue,
        },
        Vehicle: {
          value: "Asia Pacific Fund Vehicle" as unknown as CSVCellValue,
        },
        "Share Class": { value: "Class A" as unknown as CSVCellValue },
        Status: { value: "Active" as unknown as CSVCellValue },
        "Launch Date": { value: "2023-05-01" as unknown as CSVCellValue },
      },
    ],
  },
});
